import ItemLibrary from '@/components/ItemLibrary'
import Data from '@/data'
import { LibraryNode } from '@/library'
import { gemCreate, gemItemIcon, gemMaxLevel, gemName, validateGem } from '@/logic/gem'
import { CallbackWrap } from '@/types/common'
import { SkillsEmbedSkill } from '@/types/embed'
import Dialog from '@/ui/dialog'
import React from 'react'

import { getIconPath } from '@/common'
import HoverNumber from '@/components/editor/HoverNumber'
import { WithGemTemplateTooltip, WithGemTooltip, WithItemTooltip } from '@/tooltip'
import { Gem, Item } from '@/types/item'
import Select, { underlinerFunc } from '@/ui/select'
import classNames from 'classnames'

import { SmallIcon } from '@/components/Icon'
import { InventorySlotType } from '@/data/enums'
import { GemDefinition } from '@/data/types'
import { itemColor, itemGetName, itemIcon } from '@/logic/item'
import { selectItem } from '@/logic/stateReducers'
import Store from '@/store'
import { TextInput } from '@maxroll-media-group/common-libs'
import { slotIconIndex } from '.'
import { PlannerDialog } from '../utils/dialog'
import styles from './edit-skill.module.scss'

function gemCategory(filter: (gem: GemDefinition, id: string) => any, noWhite?: boolean) {
  const result: Pick<LibraryNode, 'filter' | 'children' | 'expanded'> = {
    expanded: true,
    filter,
    children: [
      {
        type: 'gem',
        name: 'Red',
        filter: (gem: GemDefinition, id: string) => filter(gem, id) && gem.tags?.includes('strength')
      },
      {
        type: 'gem',
        name: 'Green',
        filter: (gem: GemDefinition, id: string) => filter(gem, id) && gem.tags?.includes('dexterity')
      },
      {
        type: 'gem',
        name: 'Blue',
        filter: (gem: GemDefinition, id: string) => filter(gem, id) && gem.tags?.includes('intelligence')
      },
      {
        type: 'gem',
        name: 'White',
        filter: (gem: GemDefinition, id: string) =>
          filter(gem, id) &&
          !gem.tags?.includes('strength') &&
          !gem.tags?.includes('dexterity') &&
          !gem.tags?.includes('intelligence')
      }
    ]
  }
  if (noWhite) result.children!.length = 3
  return result
}

function gemCategories(): LibraryNode {
  return {
    type: 'gem',
    name: 'All Gems',
    filter: validateGem,
    children: [
      {
        type: 'gem',
        name: 'Skill Gems',
        ...gemCategory(
          (gem, id) =>
            validateGem(gem, id) && gem.active_skill && !gem.active_skill.transfigured && !gem.tags?.includes('vaal')
        )
      },
      {
        type: 'gem',
        name: 'Transfigured Gems',
        ...gemCategory(
          (gem, id) =>
            validateGem(gem, id) && gem.active_skill && gem.active_skill.transfigured && !gem.tags?.includes('vaal'),
          true
        )
      },
      {
        type: 'gem',
        name: 'Vaal Gems',
        ...gemCategory((gem, id) => validateGem(gem, id) && gem.active_skill && gem.tags?.includes('vaal'))
      },
      {
        type: 'gem',
        name: 'Support Gems',
        ...gemCategory((gem, id) => validateGem(gem, id) && gem.support_gem && !gem.tags?.includes('awakened'), true)
      },
      {
        type: 'gem',
        name: 'Awakened Supports',
        ...gemCategory((gem, id) => validateGem(gem, id) && gem.support_gem && gem.tags?.includes('awakened'), true)
      }
    ]
  }
}

function activeGemCategories(): LibraryNode {
  return {
    type: 'gem',
    name: 'All Gems',
    filter: (gem, id) => validateGem(gem, id) && gem.active_skill,
    children: [
      {
        type: 'gem',
        name: 'Skill Gems',
        ...gemCategory(
          (gem, id) =>
            validateGem(gem, id) && gem.active_skill && !gem.active_skill.transfigured && !gem.tags?.includes('vaal')
        )
      },
      {
        type: 'gem',
        name: 'Transfigured Gems',
        ...gemCategory(
          (gem, id) =>
            validateGem(gem, id) && gem.active_skill && gem.active_skill.transfigured && !gem.tags?.includes('vaal'),
          true
        )
      },
      {
        type: 'gem',
        name: 'Vaal Gems',
        ...gemCategory((gem, id) => validateGem(gem, id) && gem.active_skill && gem.tags?.includes('vaal'))
      }
    ]
  }
}

export function renderLibrary(
  items: string[],
  onClick: (id: string) => void,
  search: RegExp | undefined,
  highlight?: Set<string>
) {
  return (
    <div className={`poe-item-list ${styles.GemLibrary}`}>
      <div className={styles.GemLibrary__list}>
        {items.map((id) => {
          return (
            <WithGemTemplateTooltip id={id} key={id} touchAction="none">
              <div className={classNames(styles.GemLibrary__gem, highlight?.has(id) && styles.active)} lang="en">
                <div
                  className={styles.GemLibrary__icon}
                  style={{
                    backgroundImage: `url('${getIconPath(gemItemIcon(Data.gems[id])!)}')`
                  }}
                  onClick={() => onClick(id)}
                />
                <div className={styles.GemLibrary__label}>{underlinerFunc(gemName(Data.gems[id]), search)}</div>
              </div>
            </WithGemTemplateTooltip>
          )
        })}
      </div>
    </div>
  )
}

const slotOptions: { slot: InventorySlotType | ''; name: string }[] = [
  { slot: '', name: 'Any Slot' },
  { slot: 'Amulet', name: 'Amulet' },
  { slot: 'BodyArmour', name: 'Body Armour' },
  { slot: 'Boots', name: 'Boots' },
  { slot: 'Gloves', name: 'Gloves' },
  { slot: 'Helm', name: 'Helm' },
  { slot: 'Ring', name: 'Ring 1' },
  { slot: 'Ring2', name: 'Ring 2' },
  { slot: 'Weapon', name: 'Weapon' },
  { slot: 'Offhand', name: 'Offhand' },
  { slot: 'Weapon2', name: 'Weapon (II)' },
  { slot: 'Offhand2', name: 'Offhand (II)' }
]

type SlotOption = {
  id: InventorySlotType | ''
  name: string
  item?: Item
}

function renderSlot(slot: SlotOption, search?: RegExp) {
  if (slot.item) {
    return (
      <WithItemTooltip item={slot.item}>
        <span className={classNames(styles.SlotOption, `poe-item-${itemColor(slot.item)}`)}>
          <SmallIcon icon={itemIcon(slot.item)} />
          <span>{underlinerFunc(slot.name, search)}</span>
        </span>
      </WithItemTooltip>
    )
  } else {
    return (
      <span className={classNames(styles.SlotOption, styles.empty)}>
        <span
          className={styles.SlotOption__icon}
          style={{ backgroundPositionX: `-${slotIconIndex(slot.id || undefined)}em` }}
        />
        <span>{underlinerFunc(slot.name, search)}</span>
      </span>
    )
  }
}

const emptySkill: SkillsEmbedSkill = {
  gems: []
}

function SkillEditor({
  skill,
  onChange,
  onClose,
  onlyActiveSkills,
  showSlotEditor
}: {
  skill: SkillsEmbedSkill
  onChange: (value: CallbackWrap<SkillsEmbedSkill | undefined>) => void
  onClose: () => void
  onlyActiveSkills: boolean
  showSlotEditor: boolean
}) {
  const changeGem = (index: number, func: (prev: Gem) => Gem | undefined) =>
    onChange((skill) => {
      if (!skill) return skill
      const prev = skill.gems[index]
      if (!prev) return skill
      const value = func(prev)
      if (value === prev) return skill
      skill = { ...skill }
      skill.gems = skill.gems.slice()
      if (value) {
        skill.gems[index] = value
      } else {
        skill.gems.splice(index, 1)
      }
      return skill
    })

  const onAdd = React.useCallback(
    (id: string) =>
      onChange((skill) => {
        if (!skill) skill = emptySkill
        const index = skill.gems.findIndex((g) => g.id === id)
        if (index >= 0) {
          skill = { ...skill }
          skill.gems = skill.gems.slice()
          skill.gems.splice(index, 1)
        } else if (skill.gems.length < 6) {
          skill = {
            ...skill,
            gems: [...skill.gems, gemCreate(id)]
          }
        }
        return skill
      }),
    [onChange]
  )

  const categories = React.useMemo(
    () => (onlyActiveSkills ? activeGemCategories() : gemCategories()),
    [onlyActiveSkills]
  )
  const activeList = React.useMemo(() => new Set(skill.gems.map((gem) => gem.id)), [skill])

  const items = Store.useSelector((state) => state.items)
  const equipped = Store.usePlanner((profile) => profile.equipment.variants[profile.equipment.active]?.items)

  const options = React.useMemo(
    (): SlotOption[] =>
      slotOptions.map(({ slot, name }) => {
        const item = slot ? selectItem(items, equipped?.[slot]) : undefined
        if (item) return { id: slot, name: itemGetName(item), item }
        else return { id: slot, name }
      }),
    [items, equipped]
  )

  return (
    <div className={styles.Skill}>
      <div className={styles.Skill__header}>
        <div className={styles.Skill__headerLeft}>
          {showSlotEditor ? (
            <Select
              options={options}
              value={options.find((opt) => opt.id === skill.slot) ?? options[0]}
              onChange={(opt) => opt && onChange((skill) => ({ ...(skill ?? emptySkill), slot: opt.id || undefined }))}
              renderOption={renderSlot}
              renderValue={renderSlot}
              className={styles.Skill__slot}
            />
          ) : null}

          <div className={styles.Skill__preview}>
            {skill.gems.map((gem, index) => {
              const data = Data.gems[gem.id]
              if (!data || !data.base_item) return null
              const maxLevel = gemMaxLevel(gem.id)
              const maxWithCorruption = maxLevel + 1

              return (
                <WithGemTooltip
                  gem={gem}
                  key={index}
                  actions={[
                    {
                      text: 'Remove',
                      action() {
                        if (index) changeGem(index, () => undefined)
                      }
                    }
                  ]}>
                  <div className={styles.Skill__gem}>
                    <div
                      className={styles.Skill__icon}
                      style={{
                        backgroundImage: `url(${getIconPath(gemItemIcon(data)!)})`
                      }}
                      onClick={() => changeGem(index, () => undefined)}
                    />
                    {maxLevel > 1 && (
                      <div>
                        L
                        <HoverNumber
                          value={gem.level}
                          onChange={(level) => changeGem(index, (gem) => ({ ...gem, level }))}
                          min={1}
                          max={maxWithCorruption}
                        />
                      </div>
                    )}
                    <div>
                      Q
                      <HoverNumber
                        value={gem.quality}
                        onChange={(quality) => changeGem(index, (gem) => ({ ...gem, quality }))}
                        min={0}
                        max={23}
                      />
                    </div>
                  </div>
                </WithGemTooltip>
              )
            })}
            {[...Array(6 - skill.gems.length)].map((_, i) => (
              <div className={classNames(styles.Skill__gem, styles.empty)} key={i}>
                <div className={styles.Skill__icon} />
              </div>
            ))}
          </div>
        </div>
        <div className={styles.Skill__options}>
          <button
            onClick={() => {
              onChange(undefined)
              onClose()
            }}>
            Delete
          </button>
          <TextInput
            size="sm-md"
            placeholder="Name"
            value={skill.text ?? ''}
            onChange={(newValue) => {
              onChange((group) => group && { ...group, text: newValue })
            }}
          />
        </div>
      </div>
      <ItemLibrary
        root={categories}
        onClick={(_e, id) => onAdd(id)}
        renderer={(items, node, search) => renderLibrary(items, onAdd, search, activeList)}
      />
    </div>
  )
}

export function PlannerEditSkill({
  skill,
  onChange,
  onClose,
  onlyActiveSkills = false,
  showSlotEditor = true
}: {
  skill: SkillsEmbedSkill | undefined
  onChange: (value: CallbackWrap<SkillsEmbedSkill | undefined>) => void
  onClose: () => void
  onlyActiveSkills?: boolean
  showSlotEditor?: boolean
}) {
  return (
    <PlannerDialog modal className={styles.SkillEditor} title="Edit Skill" onClose={onClose}>
      <div className={styles.SkillEditor__content}>
        <SkillEditor
          skill={skill ?? emptySkill}
          onChange={onChange}
          onClose={onClose}
          onlyActiveSkills={onlyActiveSkills}
          showSlotEditor={showSlotEditor}
        />
      </div>
      <Dialog.Footer>
        <button onClick={onClose}>Confirm</button>
      </Dialog.Footer>
    </PlannerDialog>
  )
}
